import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import { getApiUrl } from '../../lib/api';
import { getCountryLanguage, translateContent } from '../../lib/translation';
import type { CountryProps } from '../../pages/Cannabis';

interface Destination {
  id: number;
  country: string;
  country_code?: string;
  city: string;
  tourist_legal_status?: string;
  can_tourists_buy?: string;
  min_age?: number;
  where_to_buy?: string;
  possession_limit?: string;
  consumption_rules?: string;
  avg_price_gram?: string;
  popular_strains?: string[];
  top_spots?: string[];
  cultural_tips?: string;
  warnings?: string;
  best_season?: string;
  vibe?: string;
  description?: string;
  last_updated?: string;
  data_source?: string;
  flag_count?: number;
  needs_review?: boolean;
}

interface CountryData {
  country: string;
  code: string;
  cities: Destination[];
  status: string;
}

import { countryFlag, statusColor } from '../../lib/country-utils';
import TripReportList from '../TripReportList';
import TripReportForm from '../TripReportForm';

function canBuyColor(canBuy?: string | null) {
  const s = typeof canBuy === 'string' ? canBuy.toLowerCase() : '';
  if (s === 'yes') return 'text-green-400';
  if (s === 'no' || s.includes('residents')) return 'text-red-400';
  return 'text-amber-400';
}

function freshnessBadge(lastUpdated?: string): { label: string; className: string } | null {
  if (!lastUpdated) return null;
  const days = Math.floor((Date.now() - new Date(lastUpdated).getTime()) / 86400000);
  if (days < 90) return { label: '● Fresh', className: 'bg-green-500/20 text-green-300 border-green-500/30' };
  if (days < 180) return { label: '◐ Aging', className: 'bg-amber-500/20 text-amber-300 border-amber-500/30' };
  return { label: '◯ Stale', className: 'bg-red-500/20 text-red-300 border-red-500/30' };
}

function dataSourceLabel(source?: string): string {
  if (!source) return '';
  const map: Record<string, string> = {
    'manual-seed': 'Manually seeded',
    'community-updated': 'Community updated',
    'admin-verified': 'Admin verified',
    'scraper-flagged': 'Pending review',
  };
  return map[source] ?? source;
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />;
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="py-2.5 border-b border-white/5 last:border-0">
      <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1 font-medium">{label}</p>
      <p className="text-white/75 text-xs leading-relaxed">{value}</p>
    </div>
  );
}

export default function TourismTab({ userCountry, isGlobal }: CountryProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [countries, setCountries] = useState<CountryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCity, setExpandedCity] = useState<number | null>(null);
  const [flaggingCityId, setFlaggingCityId] = useState<number | null>(null);
  const [flagSubmitted, setFlagSubmitted] = useState<Set<number>>(new Set());
  const [flagForm, setFlagForm] = useState({ reason: 'Outdated', user_note: '' });
  const [showingTripForm, setShowingTripForm] = useState<string | null>(null);
  const [translatingTo, setTranslatingTo] = useState<string | null>(null);
  const [translatedContent, setTranslatedContent] = useState<Record<string, string>>({});

  // Get selected country from URL params
  const selectedCountry = searchParams.get('country');

  // Update URL when country selection changes
  const handleSelectCountry = (countryCode: string | null) => {
    if (countryCode) {
      searchParams.set('country', countryCode);
      setSearchParams(searchParams);
    } else {
      searchParams.delete('country');
      setSearchParams(searchParams);
    }
    setExpandedCity(null);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch(getApiUrl('/api/cannabis-tourism'));
        if (!res.ok) throw new Error('Failed');
        const data: Destination[] = await res.json();

        // Group by country
        const map = new Map<string, CountryData>();
        for (const d of data) {
          if (!map.has(d.country)) {
            map.set(d.country, {
              country: d.country,
              code: d.country_code || '',
              cities: [],
              status: d.tourist_legal_status || '',
            });
          }
          map.get(d.country)!.cities.push(d);
        }
        setCountries(Array.from(map.values()).sort((a, b) => b.cities.length - a.cities.length));
      } catch {
        setCountries([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Auto-select user's country when not in global mode
  useEffect(() => {
    if (userCountry && !isGlobal && countries.length > 0 && !selectedCountry) {
      const match = countries.find(c => c.country === userCountry.name);
      if (match) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('country', match.country);
        setSearchParams(newParams);
      }
    }
  }, [userCountry, isGlobal, countries, selectedCountry]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[...Array(9)].map((_, i) => <Skeleton key={i} className="h-28" />)}
      </div>
    );
  }

  if (countries.length === 0) {
    return (
      <div className="text-center py-16 text-white/40">
        <p>No tourism destinations available yet.</p>
      </div>
    );
  }

  // ── COUNTRY LIST ──
  if (!selectedCountry) {
    return (
      <div className="space-y-6">
        {/* Hero Banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-gradient-to-r from-blue-600/20 via-cyan-600/10 to-blue-500/5 border border-blue-500/30 p-6 md:p-8"
        >
          <div className="space-y-2">
            <p className="text-blue-400 text-sm font-bold uppercase tracking-wider">Cannabis Tourism 101</p>
            <h2 className="text-3xl md:text-4xl font-black text-white">
              ▶ Global Cannabis Destinations
            </h2>
            <p className="text-white/70 text-sm max-w-2xl">
              Practical guides to 19+ cannabis-friendly cities worldwide. Learn where to go, what to expect, and how to stay legal in each destination.
            </p>
          </div>
        </motion.div>

        {/* Description */}
        <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.03] border border-white/10">
          <span className="text-2xl">◆</span>
          <div>
            <p className="text-white font-semibold text-sm">Travel Smart</p>
            <p className="text-white/60 text-xs">Each destination has practical info on coffee shops, prices, laws, and local tips</p>
          </div>
        </div>

        {/* Countries Grid */}
        <div>
          <p className="text-white/70 text-sm font-semibold mb-4">Select a destination to explore</p>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          >
          {countries.map(c => (
            <motion.button
              key={c.country}
              variants={staggerItem}
              onClick={() => handleSelectCountry(c.country)}
              whileHover={{ scale: 1.05, y: -3 }}
              className="group text-left rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] hover:from-blue-600/15 hover:to-white/[0.03] hover:border-blue-500/30 p-5 transition-all duration-200"
            >
              <div className="flex items-start gap-3 mb-3">
                <span className="text-4xl leading-none">{countryFlag(c.code)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-base">{c.country}</p>
                  <p className="text-blue-400 text-xs font-semibold mt-1">
                    ● {c.cities.length} {c.cities.length === 1 ? 'city' : 'cities'}
                  </p>
                </div>
                <span className="text-white/30 group-hover:text-blue-400 group-hover:scale-125 transition-all mt-1 text-lg">→</span>
              </div>

              <span className={`inline-block px-3 py-1 text-[11px] font-bold rounded-full border ${statusColor(c.status)}`}>
                {c.status}
              </span>

              {c.cities.length > 0 && c.cities[0].vibe && (
                <p className="text-white/50 text-xs mt-3 line-clamp-2 italic border-t border-white/5 pt-3">
                  "{c.cities[0].vibe}"
                </p>
              )}
            </motion.button>
          ))}
        </motion.div>
        </div>
      </div>
    );
  }

  // ── COUNTRY DETAIL ──
  const countryData = countries.find(c => c.country === selectedCountry);
  if (!countryData) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-4 flex-1">
          <motion.button
            onClick={() => handleSelectCountry(null)}
            whileHover={{ scale: 1.1 }}
            className="shrink-0 w-10 h-10 flex items-center justify-center rounded-xl border border-white/20 text-white/60 hover:text-white hover:border-blue-500/50 hover:bg-blue-500/10 transition-all"
          >
            ←
          </motion.button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-4xl">{countryFlag(countryData.code)}</span>
              <div>
                <h3 className="text-white font-black text-2xl">{countryData.country}</h3>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 text-xs font-bold rounded-full border ${statusColor(countryData.status)}`}>
                {countryData.status}
              </span>
              <span className="text-blue-400 text-xs font-semibold">
                ● {countryData.cities.length} {countryData.cities.length === 1 ? 'city' : 'cities'}
              </span>
            </div>
          </div>
        </div>
        {/* Translate button */}
        {(() => {
          const langInfo = getCountryLanguage(countryData.code);
          return langInfo && langInfo.code !== 'en' ? (
            <motion.button
              whileHover={{ scale: 1.05 }}
              onClick={async () => {
                if (translatingTo === langInfo.code) {
                  setTranslatingTo(null);
                  setTranslatedContent({});
                  return;
                }
                setTranslatingTo(langInfo.code);

                // Collect all visible text content to translate
                const textsToTranslate = [
                  countryData.cities
                    .filter(c => expandedCity === c.id)
                    .map(c => [
                      c.description || '',
                      c.where_to_buy || '',
                      c.consumption_rules || '',
                      c.cultural_tips || '',
                      c.warnings || ''
                    ].filter(t => t).join(' '))
                    .join(' ')
                ].filter(t => t);

                if (textsToTranslate.length === 0) return;

                try {
                  const translated = await translateContent(
                    textsToTranslate[0],
                    langInfo.code,
                    langInfo.name
                  );
                  setTranslatedContent({ main: translated });
                } catch (err) {
                  console.error('Translation failed:', err);
                  setTranslatingTo(null);
                }
              }}
              className={`shrink-0 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                translatingTo === langInfo.code
                  ? 'bg-amber-600 text-white border border-amber-500'
                  : 'bg-amber-600/20 text-amber-300 border border-amber-500/30 hover:bg-amber-600/30'
              }`}
              title={`Translate to ${langInfo.native}`}
            >
              🌍 {langInfo.native}
            </motion.button>
          ) : null;
        })()}
      </div>

      {/* Cities */}
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="space-y-3"
      >
        {countryData.cities.map(city => {
          const isExpanded = expandedCity === city.id;
          return (
            <motion.div
              key={city.id}
              variants={staggerItem}
              layout
              whileHover={{ scale: 1.02 }}
              className={`rounded-2xl border p-5 transition-all cursor-pointer ${
                isExpanded
                  ? 'border-blue-500/40 bg-gradient-to-br from-blue-600/15 to-white/[0.03]'
                  : 'border-white/10 bg-gradient-to-br from-white/[0.02] to-white/[0.005] hover:border-blue-500/30 hover:from-blue-600/10'
              }`}
            >
              {/* City header — always visible */}
              <button
                onClick={() => setExpandedCity(isExpanded ? null : city.id)}
                className="w-full text-left group"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <h4 className="text-white font-bold text-lg group-hover:text-blue-300 transition-colors">{city.city}</h4>
                    {city.vibe && (
                      <p className="text-white/50 text-xs mt-1 italic">"{city.vibe}"</p>
                    )}
                    {city.last_updated && (
                      <div className="flex items-center gap-2 mt-2">
                        {(() => {
                          const badge = freshnessBadge(city.last_updated);
                          return badge ? (
                            <>
                              <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-full border ${badge.className}`}>
                                {badge.label}
                              </span>
                              {city.data_source && (
                                <span className="text-white/30 text-[10px]">{dataSourceLabel(city.data_source)}</span>
                              )}
                            </>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-right space-y-1">
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-bold text-xs ${canBuyColor(city.can_tourists_buy)}`}>
                      {city.can_tourists_buy === 'Yes' ? '✅' : city.can_tourists_buy === 'No' ? '❌' : '⚠️'}
                      {city.can_tourists_buy === 'Yes' ? 'Buy Legal' : city.can_tourists_buy === 'No' ? 'No Buy' : 'Limited'}
                    </div>
                    {city.min_age && (
                      <p className="text-white/40 text-[11px] font-semibold">Age {city.min_age}+</p>
                    )}
                  </div>
                </div>

                {/* Quick stats — enhanced */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {city.avg_price_gram && (
                    <div className="bg-white/5 rounded-lg p-2 text-center border border-white/10">
                      <p className="text-white/40 text-[10px] font-bold">💰 PRICE</p>
                      <p className="text-white/80 text-xs font-bold mt-0.5">{city.avg_price_gram}</p>
                    </div>
                  )}
                  {city.possession_limit && (
                    <div className="bg-white/5 rounded-lg p-2 text-center border border-white/10">
                      <p className="text-white/40 text-[10px] font-bold">◇ LIMIT</p>
                      <p className="text-white/80 text-xs font-bold mt-0.5 line-clamp-1">{city.possession_limit}</p>
                    </div>
                  )}
                  {city.best_season && (
                    <div className="bg-white/5 rounded-lg p-2 text-center border border-white/10">
                      <p className="text-white/40 text-[10px] font-bold">🗓️ BEST TIME</p>
                      <p className="text-white/80 text-xs font-bold mt-0.5 line-clamp-1">{city.best_season}</p>
                    </div>
                  )}
                </div>

                <p className="text-blue-400/60 text-xs font-semibold group-hover:text-blue-400 transition-colors">
                  {isExpanded ? '▼ Collapse' : '▶ More details'}
                </p>
              </button>

              {/* Expanded detail */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 pt-4 border-t border-white/10 space-y-0">
                      {city.description && (
                        <div className="pb-3 mb-1">
                          <p className="text-white/60 text-sm leading-relaxed">{city.description}</p>
                        </div>
                      )}

                      <InfoRow label="Where to buy" value={city.where_to_buy} />
                      <InfoRow label="Consumption rules" value={city.consumption_rules} />
                      <InfoRow label="Cultural tips" value={city.cultural_tips} />
                      <InfoRow label="Warnings" value={city.warnings} />

                      {Array.isArray(city.popular_strains) && city.popular_strains.length > 0 && (
                        <div className="py-2.5 border-b border-white/5">
                          <p className="text-white/40 text-[10px] uppercase tracking-wider mb-2 font-medium">Popular strains</p>
                          <div className="flex flex-wrap gap-1.5">
                            {city.popular_strains.map((s, i) => {
                              const strainName = typeof s === 'string' ? s : String(s);
                              return (
                                <span key={i} className="px-2 py-0.5 text-[11px] rounded-full bg-green-500/10 text-green-300/70 border border-green-500/20">
                                  {strainName}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {Array.isArray(city.top_spots) && city.top_spots.length > 0 && (
                        <div className="py-2.5">
                          <p className="text-white/40 text-[10px] uppercase tracking-wider mb-2 font-medium">Notable spots</p>
                          <ul className="space-y-1">
                            {city.top_spots.map((s, i) => {
                              const spotName = typeof s === 'string' ? s : String(s);
                              return (
                                <li key={i} className="text-white/60 text-xs pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:w-1 before:h-1 before:rounded-full before:bg-white/20">
                                  {spotName}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Trip Reports Section */}
                      <div className="py-4 border-t border-white/10 mt-4">
                        <p className="text-white/40 text-[10px] uppercase tracking-wider mb-3 font-medium">✈️ Traveler Stories</p>
                        <TripReportList countryCode={countryData.code} />
                        <div className="mt-4 pt-4 border-t border-white/10">
                          {showingTripForm === `${countryData.code}-${city.id}` ? (
                            <TripReportForm
                              countryCode={countryData.code}
                              onSubmit={() => {
                                setShowingTripForm(null);
                              }}
                              onCancel={() => setShowingTripForm(null)}
                            />
                          ) : (
                            <button
                              onClick={() => setShowingTripForm(`${countryData.code}-${city.id}`)}
                              className="w-full text-amber-300 hover:text-amber-200 text-xs font-semibold py-2 rounded-lg border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 transition-all"
                            >
                              + Share your travel story
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Flag Button */}
                      {flagSubmitted.has(city.id) ? (
                        <div className="py-3 text-center">
                          <p className="text-green-400 text-sm font-semibold">✓ Thanks for the report!</p>
                        </div>
                      ) : (
                        <>
                          <div className="pt-3 border-t border-white/10 mt-3">
                            {flaggingCityId === city.id ? (
                              <div className="space-y-3">
                                <div>
                                  <label className="text-white/60 text-xs font-semibold block mb-2">Reason</label>
                                  <select
                                    value={flagForm.reason}
                                    onChange={(e) => setFlagForm({ ...flagForm, reason: e.target.value })}
                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500/50"
                                  >
                                    <option>Outdated</option>
                                    <option>Wrong Info</option>
                                    <option>Shop Closed</option>
                                    <option>Law Changed</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="text-white/60 text-xs font-semibold block mb-2">Details (optional)</label>
                                  <textarea
                                    value={flagForm.user_note}
                                    onChange={(e) => setFlagForm({ ...flagForm, user_note: e.target.value.slice(0, 300) })}
                                    maxLength={300}
                                    placeholder="Add details about the issue..."
                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500/50"
                                    rows={2}
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={async () => {
                                      try {
                                        const res = await fetch(getApiUrl(`/api/cannabis-tourism/${city.id}/flag`), {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ reason: flagForm.reason, user_note: flagForm.user_note }),
                                        });
                                        if (res.status === 429) {
                                          alert("You've already flagged this destination today");
                                        } else if (res.ok) {
                                          setFlagSubmitted(new Set([...flagSubmitted, city.id]));
                                          setFlaggingCityId(null);
                                        }
                                      } catch (err) {
                                        alert('Failed to submit report');
                                      }
                                    }}
                                    className="flex-1 bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 text-xs font-semibold px-3 py-2 rounded-lg border border-blue-500/30 transition-all"
                                  >
                                    Submit
                                  </button>
                                  <button
                                    onClick={() => {
                                      setFlaggingCityId(null);
                                      setFlagForm({ reason: 'Outdated', user_note: '' });
                                    }}
                                    className="flex-1 bg-white/5 hover:bg-white/10 text-white/60 text-xs font-semibold px-3 py-2 rounded-lg border border-white/20 transition-all"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => setFlaggingCityId(city.id)}
                                className="w-full text-blue-300 hover:text-blue-200 text-xs font-semibold py-2 rounded-lg border border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20 transition-all"
                              >
                                ⚑ Report an issue
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}
